import React, { useState, useEffect } from 'react';
import { Mail, Share2, Clock, AlertTriangle, Trophy, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Entry, Campaign, Prize } from '../../lib/supabase';

type WinnerWithDetails = Entry & {
  campaign: Campaign;
  prize?: Prize;
};

type Props = {
  winner: WinnerWithDetails;
  onClose: () => void;
  onRedraw: () => void;
};

const RESPONSE_TIMEOUT_HOURS = 24; // Time winner has to respond before redraw is allowed

const WinnerManagement: React.FC<Props> = ({ winner, onClose, onRedraw }) => {
  const [loading, setLoading] = useState(false);
  const [contactHistory, setContactHistory] = useState<{
    type: 'email';
    timestamp: string;
    status: string;
  }[]>([]);
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  useEffect(() => {
    if (!winner) return;

    fetchContactHistory();
    updateTimeRemaining();
    const timer = setInterval(updateTimeRemaining, 60000); // Update every minute
    return () => clearInterval(timer);
  }, [winner]);

  const fetchContactHistory = async () => {
    if (!winner?.email) return;

    try {
      const { data, error } = await supabase
        .from('email_logs')
        .select('*')
        .eq('email', winner.email)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setContactHistory(
        (data || []).map(log => ({
          type: 'email',
          timestamp: log.created_at,
          status: log.status
        }))
      );
    } catch (error) {
      console.error('Error fetching contact history:', error);
    }
  };

  const updateTimeRemaining = () => {
    if (!winner?.created_at) return;

    const winnerSelectedAt = new Date(winner.created_at);
    const deadline = new Date(winnerSelectedAt.getTime() + RESPONSE_TIMEOUT_HOURS * 60 * 60 * 1000);
    const now = new Date();
    const diff = deadline.getTime() - now.getTime();

    if (diff <= 0) {
      setTimeRemaining('Response deadline passed');
      return;
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    setTimeRemaining(`${hours}h ${minutes}m remaining`);
  };

  // Helper function to send winner notification
  const sendWinnerNotification = async (email: string, campaignName: string, prizeName: string) => {
    const response = await fetch('/api/send-winner-notification', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        campaignName,
        prizeName,
        timeLimit: RESPONSE_TIMEOUT_HOURS
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send winner notification');
    }

    return data;
  };

  const handleContact = async (type: 'email') => {
    if (!winner?.email || !winner?.campaign?.name) return;

    try {
      setLoading(true);
      // Send email notification
      await sendWinnerNotification(
        winner.email,
        winner.campaign.name,
        winner.prize?.name || 'prize'
      );

      // Log the email in Supabase
      const { error: logError } = await supabase
        .from('email_logs')
        .insert({
          email: winner.email,
          subject: `Winner Notification - ${winner.campaign.name}`,
          status: 'sent'
        });

      if (logError) throw logError;

      await fetchContactHistory();
    } catch (error) {
      console.error('Error contacting winner:', error);
      alert('Failed to contact winner. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!winner?.campaign?.name) return;

    const text = `Congratulations to ${winner.name || 'our winner'} for winning ${winner.campaign.name}!`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Raffle Winner Announcement',
          text,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(text);
        alert('Winner announcement copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing winner:', error);
    }
  };

  const handleRedraw = async () => {
    if (!winner?.created_at) return;

    const winnerSelectedAt = new Date(winner.created_at);
    const deadline = new Date(winnerSelectedAt.getTime() + RESPONSE_TIMEOUT_HOURS * 60 * 60 * 1000);
    const now = new Date();

    if (now < deadline) {
      alert(`Cannot redraw yet. Please wait until the ${RESPONSE_TIMEOUT_HOURS}-hour response period has passed.`);
      return;
    }

    if (window.confirm('Are you sure you want to redraw? This will select a new winner and cannot be undone.')) {
      try {
        setLoading(true);

        // Reset current winner's status
        const { error: resetError } = await supabase
          .from('entries')
          .update({
            is_winner: false,
            prize_id: null
          })
          .eq('id', winner.id);

        if (resetError) throw resetError;

        // Get new eligible entries
        const { data: eligibleEntries, error: entriesError } = await supabase
          .from('entries')
          .select('*')
          .eq('campaign_id', winner.campaign_id)
          .eq('payment_status', 'paid')
          .eq('is_winner', false);

        if (entriesError) throw entriesError;

        if (!eligibleEntries || eligibleEntries.length === 0) {
          throw new Error('No eligible entries available for redraw');
        }

        // Select new winner randomly
        const newWinnerIndex = Math.floor(Math.random() * eligibleEntries.length);
        const newWinner = eligibleEntries[newWinnerIndex];

        // Update new winner
        const { error: updateError } = await supabase
          .from('entries')
          .update({
            is_winner: true,
            prize_id: winner.prize?.id
          })
          .eq('id', newWinner.id);

        if (updateError) throw updateError;

        // Send notification to new winner
        if (winner.prize) {
          await sendWinnerNotification(
            newWinner.email,
            winner.campaign.name,
            winner.prize.name
          );
        }

        onRedraw();
      } catch (error: any) {
        console.error('Error redrawing winner:', error);
        alert(error.message || 'Failed to redraw winner. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  if (!winner || !winner.campaign) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Winner Management</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <span className="sr-only">Close</span>
            ×
          </button>
        </div>

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">{winner.name || 'Anonymous Winner'}</h3>
              <p className="text-gray-600">{winner.email}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleContact('email')}
                disabled={loading}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
              >
                <Mail className="w-4 h-4 mr-2 inline-block" />
                Contact
              </button>
              <button
                onClick={handleShare}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
              >
                <Share2 className="w-4 h-4 mr-2 inline-block" />
                Share
              </button>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded">
            <div className="flex items-center mb-2">
              <Clock className="w-4 h-4 mr-2 text-gray-500" />
              <span className="font-medium">Response Time Remaining</span>
            </div>
            <p className="text-gray-600">{timeRemaining}</p>
          </div>

          {contactHistory.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Contact History</h4>
              <div className="space-y-2">
                {contactHistory.map((contact, index) => (
                  <div key={index} className="flex items-center text-sm text-gray-600">
                    <Mail className="w-4 h-4 mr-2" />
                    <span>{new Date(contact.timestamp).toLocaleString()}</span>
                    <span className="mx-2">-</span>
                    <span>{contact.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 pt-6 border-t">
            <button
              onClick={handleRedraw}
              disabled={loading || timeRemaining !== 'Response deadline passed'}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 disabled:opacity-50 w-full"
            >
              <RefreshCw className="w-4 h-4 mr-2 inline-block" />
              Redraw Winner
            </button>
            {timeRemaining !== 'Response deadline passed' && (
              <p className="text-sm text-gray-500 mt-2">
                <AlertTriangle className="w-4 h-4 inline-block mr-1" />
                Cannot redraw until response period has ended
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WinnerManagement;